import { useState } from 'react'
import { toast } from 'sonner'
import { CircleDashed, Hourglass, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { CommentForm, SingleComment, type CommentContextMention } from '@/components/comments'
import { useCurrentUser, useProjectComments } from '@/api/hooks'
import { useAuth } from '@/hooks/use-auth'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatStatus } from '@/lib/utils'
import { useSetActionProgress } from './hooks'
import { currentProgress } from './action-card-model'
import type { PurchaseOrderAction } from './action-audit-model'

/** Where an action item's comments live: its project's comments, tagged with the item and its PO (USA-1443). */
export type ActionCommentsContext = {
  projectUuid: string
  poUuid?: string
  poNumber?: string
}

const CARD_HIDDEN_TAGS: ReadonlySet<string> = new Set(['action_item', 'purchase_order'])

/** The item's short name, as its tag reads in the project's and PO's comments. */
function actionTagText(action: PurchaseOrderAction) {
  const kind = action.card?.kind_label ?? formatStatus(action.action_type)
  return action.card?.sublabel ? `${kind} · ${action.card.sublabel}` : kind
}

/**
 * Under an action item card: its comments, a comment box, and Mark in progress (USA-1443). Comments
 * are the project's comments tagged with the item (and its PO), so they also show in the PO's and the
 * project's comments, linking back here.
 */
export function ActionWorkSection({
  action,
  poId,
  comments,
  canStart = true,
}: {
  action: PurchaseOrderAction
  poId: string
  comments?: ActionCommentsContext
  /** False for items the system opens and closes itself (an operational hold, PO review). */
  canStart?: boolean
}) {
  const [composing, setComposing] = useState(false)
  const { data: me } = useCurrentUser()
  const { user } = useAuth()
  const setProgress = useSetActionProgress(poId)
  const { data: projectComments } = useProjectComments(comments?.projectUuid ?? '')
  const isOpen = action.status === 'open'
  const working = currentProgress(action)
  const own = comments
    ? (projectComments ?? [])
        .filter((comment) =>
          comment.mentions.some(
            (mention) => mention.entity_type === 'action_item' && mention.entity_uuid === action.uuid,
          ),
        )
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
    : []
  const showControls = isOpen && (canStart || !!comments)
  if (!showControls && own.length === 0) return null

  const contextMentions: CommentContextMention[] = [
    ...(comments?.poUuid && comments.poNumber
      ? [{ entity_type: 'purchase_order', entity_uuid: comments.poUuid, display_text: comments.poNumber }]
      : []),
    { entity_type: 'action_item', entity_uuid: action.uuid, display_text: actionTagText(action) },
  ]

  return (
    <div className="space-y-3 border-t pt-3">
      {own.length > 0 && comments && (
        <div className="space-y-3" aria-label="Comments on this action item" role="list">
          {own.map((comment) => (
            <div role="listitem" key={comment.uuid}>
              <SingleComment
                comment={comment}
                projectUuid={comments.projectUuid}
                currentUserEmail={user?.email}
                omitTypes={CARD_HIDDEN_TAGS}
              />
            </div>
          ))}
        </div>
      )}
      {composing && comments && (
        <CommentForm
          projectUuid={comments.projectUuid}
          contextMentions={contextMentions}
          placeholder="Comment on this action item… Use @ to mention someone"
          onPosted={() => setComposing(false)}
        />
      )}
      {showControls && (
        <div className="flex flex-wrap gap-2">
          {canStart && (
            <LoadingButton
              size="sm"
              variant="outline"
              loading={setProgress.isPending}
              loadingText={working ? 'Updating...' : 'Starting...'}
              onClick={() =>
                setProgress.mutate(
                  { actionId: action.id, inProgress: !working, userId: me?.user_id },
                  { onError: (error) => toast.error(formatApiErrorMessage('Status not changed', error)) },
                )
              }
            >
              {working ? (
                <>
                  <CircleDashed className="mr-1 h-3 w-3" aria-hidden /> Mark not started
                </>
              ) : (
                <>
                  <Hourglass className="mr-1 h-3 w-3" aria-hidden /> Mark in progress
                </>
              )}
            </LoadingButton>
          )}
          {comments && !composing && (
            <Button size="sm" variant="ghost" onClick={() => setComposing(true)}>
              <MessageSquare className="mr-1 h-3 w-3" aria-hidden /> Comment
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
